<script lang="ts">
  import { errorsIf, formField } from "../";
  import { onMount } from "svelte";
  import FieldLabel from "./FieldLabel.svelte";

  type PatternValidationFunction = (value: string) => RegExpMatchArray | null | undefined;
  type PatternType = undefined | string | RegExp | PatternValidationFunction;

  export let name: string;
  export let key: string;
  export let icon: string | undefined = undefined;
  export let hideBorder = false;
  export let defaultValue: string | undefined = undefined;
  export let hint: string | undefined = undefined;
  export let forceLimit: boolean = false;
  export let showLimitIndicator: boolean = true;
  export let showOptionalIndicator: boolean = true;
  // generate a random id
  export let id: string = Math.random().toString(36).substr(2, 9);

  // Validation
  export let limit: number = 0;
  export let required = false;
  export let placeholder = "";
  export let autofocus = false;
  export let pattern: PatternType = undefined;
  export let disabled = false;
  export let selectionColor: string | undefined = undefined;
  export let allowInvalidOnInitialValue = false;
  export let disableErrors = false;
  export let isValid: boolean = false;

  export let size: "small" | "medium" | "large" = "medium";

  let inputEl: HTMLInputElement;

  export function setValue(newValue: string) {
    $field.value = newValue;
  }

  export function getValue() {
    return $field.value;
  }

  export function focus() {
    inputEl.select();
  }

  export function setIsPending(pending: boolean) {
    $field.isPending = pending;
  }

  const field = formField<string>(key, name);

  if (!$field.value && defaultValue) {
    $field.value = defaultValue;
  }

  function validatePattern(valueToValidate: string | undefined) {
    if (pattern && valueToValidate) {
      if (typeof pattern === "function") {
        return pattern(valueToValidate);
      }
      return valueToValidate.match(pattern);
    }
    return true;
  }

  // Reactive validations
  $: $field.errors =
    allowInvalidOnInitialValue && !$field.isDirty
      ? []
      : errorsIf(
          required && (!$field.value || $field.value.trim().length === 0) && "Required",
          !!limit && !!$field.value && $field.value!.length > limit && "Too long",
          !validatePattern($field.value) && "Invalid"
        );

  $: numOfCharsLeft = limit - ($field.value || "").length;
  $: isValid = !$field.errors.length || !$field.isDirty;

  onMount(() => {
    if (autofocus) {
      window.requestAnimationFrame(() => {
        if (inputEl) {
          inputEl.select();
        }
      });
    }
  });
</script>

<!-- svelte-ignore a11y-label-has-associated-control -->
<FieldLabel
  {name}
  {required}
  {showOptionalIndicator}
  {id}
  {hint}
  errorMessage={$field.errors.length && $field.isDirty && !disableErrors ? $field.errors[0]?.message : undefined}>
  <!-- Text Input -->
  <div class="relative">
    <input
      name={id}
      {id}
      type="text"
      bind:value={$field.value}
      disabled={$field.isDisabled || disabled}
      class:text-12={size === "small"}
      class:text-13={size === "medium"}
      class:text-16={size === "large"}
      class:small-input={size === "small"}
      class:leading-none={size === "large"}
      class=" text-input form-input block w-full rounded bg-left bg-no-repeat pr-2 {hideBorder
        ? 'border-none'
        : 'border-gray-300 focus:border-teal-400 active:border-teal-400'}
      transition-all"
      style={(hideBorder ? "--border-focus: none;" : "") + (icon ? `;background-image: url(${icon});background-position-x:.5rem` : ";")}
      class:error={!isValid && !disableErrors}
      class:pr-9={showLimitIndicator && limit > 0}
      class:bg-gray-100={disabled}
      class:pl-8={!!icon}
      class:pl-2={!icon}
      {placeholder}
      autocomplete="off"
      maxlength={forceLimit ? limit : undefined}
      bind:this={inputEl}
      on:keydown
      on:blur />

    {#if limit > 0 && showLimitIndicator}
      <span
        class="char-counter absolute inset-y-0 right-0 flex items-center justify-center pr-2 text-sm"
        class:text-gray-500={numOfCharsLeft > 9}
        class:text-warning-600={numOfCharsLeft <= 9 && numOfCharsLeft >= 0}
        class:text-danger-600={numOfCharsLeft < 0}
        class:font-medium={numOfCharsLeft <= 9}>
        {numOfCharsLeft}
      </span>
    {/if}
  </div>
</FieldLabel>

<style>
  .form-input {
    --border-focus: 0 0 0 4px #dcf4f1;
    padding-top: 0.35em;
    padding-bottom: 0.35em;
  }

  .form-input.small-input {
    padding-top: 0.15em;
    padding-bottom: 0.15em;
  }

  .form-input:focus {
    outline: none;
    box-shadow: var(--border-focus);
  }

  .form-input.error {
    border-color: #ad282585;
  }
  .form-input.error:focus {
    border-color: #ad2825;
    box-shadow: 0 0 0 4px #f9e7e7;
  }

  .form-input::selection {
    background-color: var(--selection-color);
  }

  .form-input::-moz-selection {
    background-color: var(--selection-color);
  }

  .char-counter {
    display: none;
  }

  .form-input:focus + .char-counter {
    display: flex;
  }
</style>
